import React, { useMemo, useEffect, useState } from 'react';
import { Tree } from 'antd';
import type { TreeDataNode, TreeProps } from 'antd';
import { Circle, Layers } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLibrary } from '../../context/library';
import { getFolderIcon } from '../../clients/folder-icons';
import { useClientId } from '../../hooks/use-client-id';
import { useResponsiveSizes } from '../../hooks/use-responsive-sizes';
import { getFolderName } from '../../utils';
import { palette, typography } from '@doer/frontend-components/theme';

const textColor = palette.primitives.grey.grey800;
const accent = palette.primitives.purple.purple550;
const accentBg = `${accent}1A`; // 10% alpha
const accentBgHover = `${accent}26`; // 15% alpha

const { DirectoryTree } = Tree;

const buildTreeFromFolders = (folders: string[], clientId: string): TreeDataNode[] => {
  const tree: TreeDataNode[] = [];
  const nodeMap: { [key: string]: TreeDataNode } = {};

  // PASO 1: Crear todos los nodos (incluyendo padres intermedios)
  folders.forEach(folderPath => {
    const pathParts = folderPath.split('/');

    for (let index = 0; index < pathParts.length; index++) {
      const currentPath = pathParts.slice(0, index + 1).join('/');

      if (!nodeMap[currentPath]) {
        const isRootLevel = index === 0;
        const label = getFolderName(currentPath);

        nodeMap[currentPath] = {
          title: label,
          key: currentPath,
          isLeaf: true,
          icon: (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              {isRootLevel ? (
                getFolderIcon(currentPath, clientId)
              ) : (
                <Circle size={10} fill={palette.primitives.grey.grey300} strokeWidth={0} />
              )}
            </div>
          ),
          children: []
        };
      }
    }
  });

  // PASO 2: Armar jerarquía - iterar sobre TODOS los nodos
  Object.keys(nodeMap).forEach(folderPath => {
    const pathParts = folderPath.split('/');

    if (pathParts.length === 1) {
      tree.push(nodeMap[folderPath]);
    } else {
      const parentPath = pathParts.slice(0, -1).join('/');
      nodeMap[parentPath].children!.push(nodeMap[folderPath]);
      nodeMap[parentPath].isLeaf = false;
    }
  });

  return tree;
};

// Collapse single-child chains: a lone folder (no siblings) that has children carries
// no info, so we drop it and pull its children up. Recursive. Leaves stay (nothing to
// pull up). E.g. creativity → ESPN → {A, B}  becomes  {A, B}.
const collapseSingleChildChains = (nodes: TreeDataNode[]): TreeDataNode[] => {
  const collapsed = nodes.map(node => ({
    ...node,
    children: node.children ? collapseSingleChildChains(node.children) : node.children
  }));

  if (collapsed.length === 1 && collapsed[0].children && collapsed[0].children.length > 0) {
    return collapsed[0].children;
  }

  return collapsed;
};

// Función para obtener todos los padres de una carpeta
const getParentKeys = (folderPath: string): string[] => {
  const parts = folderPath.split('/');
  const parents: string[] = [];

  for (let i = 1; i < parts.length; i++) {
    parents.push(parts.slice(0, i).join('/'));
  }

  return parents;
};

interface TreeViewProps {
  darkMode?: boolean; // Para usar en el drawer móvil
}

export const TreeView: React.FC<TreeViewProps> = ({ darkMode = false }) => {
  const navigate = useNavigate();
  const { fontSize } = useResponsiveSizes();
  const { folders, currentFolder } = useLibrary();
  const clientId = useClientId();

  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

  const selectedStyle = {
    backgroundColor: accentBg,
    color: accent,
    border: `1px solid ${accent}4D`,
    boxShadow: 'none'
  };

  const unselectedStyle = (isDark: boolean) => ({
    backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : '#f5f5f5',
    color: isDark ? '#d1d1d1' : textColor,
    border: isDark ? '1px solid rgba(255,255,255,0.2)' : '1px solid #e5e5e5',
    boxShadow: 'none'
  });

  const selectedKeys = useMemo(() => {
    if (!currentFolder) return [];
    return [currentFolder];
  }, [currentFolder]);

  // Auto-expandir carpetas padres cuando cambia currentFolder
  useEffect(() => {
    if (currentFolder) {
      const parentKeys = getParentKeys(currentFolder);
      setExpandedKeys(prev => {
        const newKeys = new Set([...prev, ...parentKeys]);
        return Array.from(newKeys);
      });
    }
  }, [currentFolder]);

  const onSelect: TreeProps['onSelect'] = selectedKeys => {
    const folderPath = selectedKeys[0] as string;
    navigate(`/library/${encodeURIComponent(folderPath)}`);
  };

  const onExpand: TreeProps['onExpand'] = expandedKeys => {
    setExpandedKeys(expandedKeys as string[]);
  };

  const handleShowAll = () => {
    navigate('/library');
  };

  const treeData = collapseSingleChildChains(buildTreeFromFolders(folders, clientId));

  return (
    <>
      <style>
        {`
      .tree-light-mode .ant-tree-node-content-wrapper,
      .tree-light-mode .ant-tree-title {
        color: ${textColor} !important;
        font-family: ${typography.fontFamily.ui} !important;
        font-weight: ${typography.fontWeight.medium} !important;
        text-transform: uppercase;
        letter-spacing: 0.7px;
        font-size: 11px !important;
      }
      .tree-light-mode .ant-tree-node-selected .ant-tree-node-content-wrapper,
      .tree-light-mode .ant-tree-node-content-wrapper.ant-tree-node-selected {
        background-color: transparent !important;
      }
      .tree-light-mode .ant-tree-node-selected .ant-tree-title {
        color: ${accent} !important;
        font-weight: ${typography.fontWeight.semibold} !important;
      }
    `}
      </style>

      {darkMode && (
        <style>
          {`
      .tree-dark-mode .ant-tree {
        background: transparent !important;
        color: #d1d1d1 !important;
      }
      .tree-dark-mode .ant-tree-node-content-wrapper {
        color: #d1d1d1 !important;
      }
      .tree-dark-mode .ant-tree-node-content-wrapper:hover {
        background-color: rgba(255, 255, 255, 0.1) !important;
      }
      .tree-dark-mode .ant-tree-node-selected .ant-tree-node-content-wrapper {
        background-color: ${accentBg} !important;
        border: 1px solid transparent !important;
      }
      .tree-dark-mode .ant-tree-node-selected .ant-tree-title {
        color: ${accent} !important;
        font-weight: 500;
      }
      .tree-dark-mode .ant-tree-node-selected .ant-tree-iconEle svg {
        color: ${accent} !important;
        fill: ${accent} !important;
      }
      .tree-dark-mode .ant-tree-switcher {
        color: #999 !important;
      }
    `}
        </style>
      )}

      <div
        style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
        className={darkMode ? 'tree-dark-mode' : 'tree-light-mode'}
      >
        <div
          onClick={handleShowAll}
          style={{
            padding: '12px 16px',
            ...(!currentFolder ? selectedStyle : unselectedStyle(darkMode)),
            borderRadius: '12px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontFamily: typography.fontFamily.ui,
            fontWeight: typography.fontWeight.medium,
            fontSize: 11,
            textTransform: 'uppercase',
            letterSpacing: '0.8px',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={e => {
            if (!currentFolder) {
              e.currentTarget.style.backgroundColor = accentBgHover;
            }
          }}
          onMouseLeave={e => {
            if (!currentFolder) {
              e.currentTarget.style.backgroundColor = accentBg;
            }
          }}
        >
          <Layers size={16} />
          Todos
        </div>

        <DirectoryTree
          multiple={false}
          showIcon={true}
          selectedKeys={selectedKeys}
          expandedKeys={expandedKeys}
          treeData={treeData}
          onSelect={onSelect}
          onExpand={onExpand}
          style={{
            width: '100%',
            textAlign: 'left',
            fontFamily: typography.fontFamily.ui,
            fontSize: fontSize.xlg,
            background: 'transparent'
          }}
          className='custom-tree-padding'
        />
      </div>
    </>
  );
};
